import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { LookupsClient } from "../web-api-client";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { wrapFetchWithSignal } from "../utils/abort";
import { appConfig } from "../appconfig";

/** Danh sách quận theo tỉnh */
export function useDistrictOptions(provinceId?: number, timeoutMs = 10_000) {
  return useQuery({
    queryKey: ["geo", "districts", provinceId],
    enabled: !!provinceId,
    placeholderData: keepPreviousData,
    staleTime: 10 * 60_000, // cache 10 phút
    retry: (n, err: any) => {
      if (err?.name === "AbortError" || err?.name === "TimeoutError") return false;
      const s = err?.status ?? err?.response?.status;
      return s && s >= 400 && s < 500 ? false : n < 2;
    },
    queryFn: async ({ signal }) => {
      const client = new LookupsClient(appConfig.apiBaseUrl, {
        fetch: wrapFetchWithSignal(fetchWithTimeout(), signal),
      });
      return client.getDistricts(provinceId!);
    },
    // map thành options luôn, khỏi cần setState riêng
    select: (rows) =>
      rows.map(d => ({ value: d.id ?? "", label: d.name ?? "" })),
  });
}

/** Danh sách phường theo quận */
export function useWardOptions(provinceId?: number, timeoutMs = 10_000) {
  return useQuery({
    queryKey: ["geo", "wards", provinceId],
    enabled: !!provinceId,
    placeholderData: keepPreviousData,
    staleTime: 10 * 60_000,
    retry: (n, err: any) => {
      if (err?.name === "AbortError" || err?.name === "TimeoutError") return false;
      const s = err?.status ?? err?.response?.status;
      return s && s >= 400 && s < 500 ? false : n < 2;
    },
    queryFn: async ({ signal }) => {
      const client = new LookupsClient(appConfig.apiBaseUrl, {
        fetch: wrapFetchWithSignal(fetchWithTimeout(), signal),
      });
      return client.getWards(provinceId!);
    },
    select: (rows) =>
      rows.map(w => ({ value: w.id ?? "", label: w.name ?? "" })),
  });
}

/** Bundle(vùng, dân tộc, nghề, ...) */
export function useLookupBundle(enabled = true, timeoutMs = 10_000) {
  return useQuery({
    queryKey: ["lookups", "bundle"],
    enabled,
    staleTime: 24 * 60 * 60_000, // 1 ngày
    gcTime: 24 * 60 * 60_000,
    refetchOnWindowFocus: false,
    retry: (n, err: any) => {
      if (err?.name === "AbortError" || err?.name === "TimeoutError") return false;
      const s = err?.status ?? err?.response?.status;
      return s && s >= 400 && s < 500 ? false : n < 2;
    },
    queryFn: async ({ signal }) => {
      const client = new LookupsClient(appConfig.apiBaseUrl, {
        fetch: wrapFetchWithSignal(fetchWithTimeout(), signal),
      });
      return client.getLookupsBundle();
    },
  });
}
